import React from "react";
import { Link } from "react-router-dom";
import { PC_MARK } from "@/lib/assets";
import { BrandLogo } from "@/components/BrandLogo";

// One reusable PropertyConnect footer. Three variants:
//  - full    : public discovery / information surfaces (navy, brand + link columns)
//  - compact : authenticated professional workspace (restrained institutional/legal bar)
//  - minimal : authentication flows (centered, unobtrusive)
// Static / router-based only — zero API / Match Engine / OpenAI calls.

const YEAR = new Date().getFullYear();
const CORP = "PropertyConnect is operated by KODA Technologies Inc.";
const COPYRIGHT = `© ${YEAR} PropertyConnect. All rights reserved.`;

const EXPLORE = [
  ["Marketplace", "/marketplace"],
  ["Buy", "/marketplace?type=For%20Sale"],
  ["Rent", "/marketplace?type=For%20Rent"],
  ["Stays", "/marketplace?type=Unit%20Hosting"],
  ["Sell", "/sell"],
];
const FOR_PROS = [
  ["Sign in", "/login"],
  ["Create account", "/register"],
];
const RESOURCES = [
  ["About PropertyConnect", "/about"],
  ["Help & FAQs", "/help"],
  ["Contact Us", "/contact"],
];
const LEGAL = [
  ["Terms of Service", "/terms"],
  ["Privacy Policy", "/privacy"],
];
const COMPACT_LINKS = [
  ["About", "/about"],
  ["Help", "/help"],
  ["Contact", "/contact"],
  ["Terms", "/terms"],
  ["Privacy", "/privacy"],
];

function Column({ title, links }) {
  return (
    <div>
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50 mb-3.5">{title}</h3>
      <ul className="space-y-2.5">
        {links.map(([label, to]) => (
          <li key={label}>
            <Link
              to={to}
              className="text-sm text-white/80 hover:text-[#C79A3B] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C79A3B] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F2C59] rounded"
            >
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FullFooter() {
  return (
    <footer
      role="contentinfo"
      data-testid="site-footer-full"
      className="bg-gradient-to-b from-[#143163] to-[#0F2C59] border-t border-[#C79A3B]/25 mt-16"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-14">
        <div className="grid gap-10 md:grid-cols-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <Link to="/marketplace" aria-label="PropertyConnect marketplace" className="inline-flex items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C79A3B] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F2C59]">
              <BrandLogo plate className="h-[80px]" />
            </Link>
            <p className="text-sm text-white/70 mt-5 max-w-sm leading-relaxed">
              The Intelligent Real Estate Network for the Philippines — connecting professionals, properties and active buyer demand.
            </p>
            <p className="text-[13px] sm:text-sm font-semibold tracking-[0.14em] text-[#C79A3B] mt-4">CONNECT • MATCH • COLLABORATE • CLOSE</p>
          </div>
          {/* Link columns */}
          <nav aria-label="Footer" className="md:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-8">
            <Column title="Explore" links={EXPLORE} />
            <Column title="For Professionals" links={FOR_PROS} />
            <Column title="Resources" links={RESOURCES} />
            <Column title="Legal" links={LEGAL} />
          </nav>
        </div>
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <p className="text-xs text-white/60">{COPYRIGHT}</p>
          <p className="text-xs text-white/50">{CORP}</p>
        </div>
      </div>
    </footer>
  );
}

function CompactFooter() {
  return (
    <footer
      role="contentinfo"
      data-testid="site-footer-compact"
      className="border-t border-[#C79A3B]/25 bg-gradient-to-b from-[#143163] to-[#0F2C59] mt-10"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-white/75 text-center sm:text-left">
          {COPYRIGHT} <span className="text-white/30 mx-1">·</span> {CORP}
        </p>
        <nav aria-label="Footer" className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5">
          {COMPACT_LINKS.map(([label, to]) => (
            <Link
              key={to}
              to={to}
              className="text-xs font-medium text-white/80 hover:text-[#C79A3B] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C79A3B] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F2C59] rounded"
            >
              {label}
            </Link>
          ))}
        </nav>
      </div>
    </footer>
  );
}

function MinimalFooter() {
  return (
    <footer role="contentinfo" data-testid="site-footer-minimal" className="mt-8 text-center">
      <nav aria-label="Footer" className="flex items-center justify-center gap-2.5 text-xs flex-wrap">
        {LEGAL.map(([label, to], i) => (
          <React.Fragment key={to}>
            <Link
              to={to}
              className="text-slate-600 hover:text-[#0F2C59] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F2C59] rounded"
            >
              {label}
            </Link>
            {i < LEGAL.length - 1 && <span className="text-slate-300">·</span>}
          </React.Fragment>
        ))}
      </nav>
      <p className="text-xs text-slate-500 mt-2">{COPYRIGHT}</p>
      <p className="text-xs text-slate-500 mt-0.5">{CORP}</p>
    </footer>
  );
}

export function SiteFooter({ variant = "full" }) {
  if (variant === "compact") return <CompactFooter />;
  if (variant === "minimal") return <MinimalFooter />;
  return <FullFooter />;
}

export default SiteFooter;
